// Hostname + HTTPS setup: point a DNS A record at this box, issue a Let's Encrypt
// certificate, and switch the hostname to HTTPS. Access by IP keeps working either way.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Tls = {
  hostname: string | null;
  admin_email: string | null;
  public_ip: string | null;
  dns: { resolves_to: string[]; points_here: boolean };
  cert: { subject: string; valid_to: string | null; issuer: string } | null;
  expires: string | null;
  last_run: { state: string; message?: string; at?: string } | null;
  https_ready: boolean;
};

export default function HostnameTls() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery<Tls>({
    queryKey: ['tls-status'],
    queryFn: () => api.get('/bead/tls'),
    refetchInterval: (q: any) => (q?.state?.data?.last_run?.state === 'running' ? 4000 : false),
  });
  const [host, setHost] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [log, setLog] = useState<string>('');

  useEffect(() => {
    if (data?.hostname && !host) setHost(data.hostname);
    if (data?.admin_email && !email) setEmail(data.admin_email);
  }, [data?.hostname, data?.admin_email]);

  const running = data?.last_run?.state === 'running';
  const failed = data?.last_run?.state === 'failed';

  const issue = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post('/bead/tls/provision', { hostname: host.trim().toLowerCase(), admin_email: email.trim() || null });
      qc.invalidateQueries({ queryKey: ['tls-status'] });
      setTimeout(() => refetch(), 2000);
    } catch (e: any) { setErr(e?.message ?? 'Could not start certificate issuance'); }
    finally { setBusy(false); }
  };

  const showLog = async () => { try { const r = await api.get('/bead/tls/log'); setLog(r.log || '(empty)'); } catch { setLog('(unavailable)'); } };

  const inp: React.CSSProperties = { padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, width: '100%' };
  const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginTop: 18 };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Hostname &amp; HTTPS</h2>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Use your own web address with a real certificate. Your team keeps working at the IP either way.
          </div>
        </div>
        {data?.https_ready && (
          <span style={{ background: '#dcfce7', color: '#065f46', borderRadius: 4, padding: '3px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            ● HTTPS LIVE
          </span>
        )}
      </div>

      {/* step 1: DNS */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, fontSize: 12.5, color: '#334155', margin: '10px 0' }}>
        <b>1. Point your DNS here first.</b> Create an <b>A record</b> for your hostname pointing at{' '}
        <code style={{ background: '#e2e8f0', padding: '1px 6px', borderRadius: 3, fontWeight: 700 }}>{data?.public_ip ?? '…'}</code>,
        then come back. A certificate can only be issued once the name points at this box.
        {data?.hostname && (
          <div style={{ marginTop: 6 }}>
            {data.dns.points_here
              ? <span style={{ color: '#065f46' }}>✓ {data.hostname} points here.</span>
              : <span style={{ color: '#b45309' }}>
                  {data.dns.resolves_to.length
                    ? `⚠ ${data.hostname} currently points at ${data.dns.resolves_to.join(', ')} — not this box.`
                    : `⚠ ${data.hostname} doesn't resolve yet (DNS can take a few minutes).`}
                </span>}
          </div>
        )}
      </div>

      {/* step 2: issue */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, color: '#475569', flex: '1 1 260px' }}>
          <div style={{ marginBottom: 3 }}>Your hostname</div>
          <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="build.acmefiber.com" style={inp} />
        </label>
        <label style={{ fontSize: 11, color: '#475569', flex: '1 1 220px' }}>
          <div style={{ marginBottom: 3 }}>Admin email (certificate expiry notices)</div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@acmefiber.com" style={inp} />
        </label>
        <button onClick={issue} disabled={busy || running || !host.trim()}
          className="btn btn--primary"
          style={{ padding: '7px 16px', border: 'none', borderRadius: 4, background: host.trim() && !running ? '#2563eb' : '#94a3b8', color: '#fff', cursor: host.trim() && !running ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
          {running ? 'Issuing…' : data?.https_ready ? 'Re-issue certificate' : 'Issue certificate'}
        </button>
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 10px', borderRadius: 4, fontSize: 12.5, marginTop: 10 }}>{err}</div>}

      {running && <div style={{ marginTop: 10, fontSize: 12.5, color: '#b45309' }}>Issuing the certificate — about a minute. This page updates itself.</div>}

      {failed && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '8px 10px', borderRadius: 4, fontSize: 12.5, marginTop: 10 }}>
          {data?.last_run?.message ?? 'Certificate issuance failed.'}{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); showLog(); }} style={{ color: '#92400e', textDecoration: 'underline' }}>See the log</a>
        </div>
      )}

      {data?.https_ready && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#065f46', padding: '9px 11px', borderRadius: 4, fontSize: 12.5, marginTop: 10 }}>
          <b>https://{data.hostname}</b> is live — <code>http://</code> redirects to it automatically.
          {data.expires && <> Certificate renews automatically; current one expires <b>{data.expires}</b>.</>}
          <div style={{ marginTop: 4, color: '#3f6212' }}>This box also stays reachable at http://{data.public_ip} for your team.</div>
        </div>
      )}

      {log && (
        <pre style={{ marginTop: 10, background: '#0f172a', color: '#e2e8f0', padding: 10, borderRadius: 6, fontSize: 11, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{log}</pre>
      )}
    </div>
  );
}
